import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaBars, FaTimes, FaPlus, FaCalendarAlt, FaImages } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Optimized image imports
const images = [
  require('../assets/images/37.jpg'),
  require('../assets/images/39.jpg'),
  require('../assets/images/36.jpg'),
  require('../assets/images/38.jpg'),
];

const frameOptions = [
  require('../assets/images/frame1.JPG'),
  require('../assets/images/frame2.JPG'),
  require('../assets/images/frame3.JPG'),
  require('../assets/images/frame4.JPG'),
  require('../assets/images/frame5.JPG'),
  require('../assets/images/frame6.JPG'),
  require('../assets/images/frame7.JPG'),
  require('../assets/images/frame8.JPG'),
  require('../assets/images/frame9.JPG')
];

const Home = ({ isModalOpen, setIsModalOpen, isFrameModalOpen, setIsFrameModalOpen }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isFrameModalOpen, setIsFrameModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventTime: '',
    eventType: '',
  });
  const [frameFormData, setFrameFormData] = useState({
    frameSize: '',
    photos: [],
    selectedFrame: null,
    name: '',
    phoneNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);

  const categories = useMemo(() => [
    { name: t("categories.babyShower") },
    { name: t("categories.pubertyFunction") },
    { name: t("categories.Consecration") },
    { name: t("categories.earPiercingCeremony") },
    { name: t("categories.tonsuringCeremony") },
    { name: t("categories.houseWarmingCeremony") },
    { name: t("categories.wedding") },
    { name: t("categories.firstHolyCommunion") },
    { name: t("categories.birthday") },
    { name: t("categories.baptism") },
    { name: t("categories.kovilKodaiVila") },
    { name: t("categories.nameCeremonyFunction") },
    { name: t("categories.others") },
  ], [t]);

  // Performance optimizations
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);



  const validatePhoneNumber = (phone) => {
    if (phone.length < 10) {
      setError(t('errors.phoneNumberInvalid'));
      return false;
    }
    setError('');
    return true;
  };

  const validateForm = () => {
    const { name, phone, eventDate, eventTime, eventType } = formData;
    if (!name || !phone || !eventDate || !eventTime || !eventType) {
      setError(t('errors.allFieldsRequired'));
      return false;
    }
    if (!validatePhoneNumber(phone)) {
      return false;
    }
    return true;
  };

  const validateFrameForm = () => {
    const { name, phoneNumber, frameSize, photos } = frameFormData;
    if (!name || !phoneNumber || !frameSize || photos.length === 0) {
      setError(t('errors.allFieldsRequired'));
      return false;
    }
    if (!validatePhoneNumber(phoneNumber)) {
      return false;
    }
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFrameInputChange = (e) => {
    const { name, value } = e.target;
    setFrameFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const removeImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
    setFrameFormData((prevData) => ({
      ...prevData,
      photos: prevData.photos.filter((_, i) => i !== index),
    }));
  };

  const handleFrameSelection = (frame) => {
    setFrameFormData((prevData) => ({ ...prevData, selectedFrame: frame }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.eventDate ||
      !formData.eventTime || !formData.eventType) {
      alert("Please fill in all required fields before submitting.");
      return;
    }
    setLoading(true);
    setError(null);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const rawData = {
        name: formData.name,
        contact: formData.phone,
        event_date: formData.eventDate,
        event_time: formData.eventTime,
        event_type: formData.eventType,
      };

      const response = await axios.post(
        'https://backend.newbrindha.com/photostudio/user/eventform',
        JSON.stringify(rawData),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        alert(t('slotBookedSuccess'));
        setIsModalOpen(false);
        setFormData({ name: '', email: '', phone: '', eventDate: '', eventTime: '', eventType: '' });
      } else {
        throw new Error(t('errors.failedToBookSlot'));
      }
    } catch (err) {
      setError(err.message || t('errors.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setFrameFormData((prevData) => ({
      ...prevData,
      photos: files,
    }));
    const imageUrls = files.map((file) => URL.createObjectURL(file));
    setUploadedImages(imageUrls);
  };

  const handleFrameFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateFrameForm()) {
      return;
    }

    const formData = new FormData();
    formData.append("frame_name", frameFormData.name);
    formData.append("phone_number", frameFormData.phoneNumber);
    formData.append("frame_size", frameFormData.frameSize);

    if (frameFormData.selectedFrame) {
      const frameBlob = await fetch(frameFormData.selectedFrame).then((res) => res.blob());
      formData.append("frame_colors", frameBlob, "frame.jpg");
    } else {
      setError(t('errors.frameColorMissing'));
      return;
    }

    if (frameFormData.photos.length > 0) {
      frameFormData.photos.forEach((photo, index) => {
        formData.append("user_photos", photo, photo.name || `photo_${index}.jpg`);
      });
    } else {
      setError(t('errors.noPhotosSelected'));
      return;
    }

    try {
      setLoading(true);
      const response = await fetch("https://backend.newbrindha.com/photostudio/user/product_frame", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || t('errors.submissionFailed'));
      }

      const result = await response.json();
      //console.log("Success:", result);
      setIsFrameModalOpen(false);
      setFrameFormData({
        frameSize: '',
        photos: [],
        selectedFrame: null,
        name: '',
        phoneNumber: '',
      });
      setUploadedImages([]);
    } catch (error) {
      setError(error.message || t('errors.genericError'));
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="relative min-h-screen">

      {/* <div className="min-h-screen flex items-center justify-center p-8 relative mt-16"> */}
      <div className="absolute inset-0 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 z-30">
        {images.map((img, index) => (
          <div key={index} className="relative overflow-hidden">
            <img
              src={img.default || img}
              alt={`Background ${index + 1}`}
              className="w-full h-full object-cover transform transition-transform duration-500 hover:scale-125 hover:z-10"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-black bg-opacity-20 z-20"></div>

      {/* <h1 className="text-4xl md:text-6xl font-semibold text-white text-center z-30">
          {t('welcomeText')}
        </h1> */}

      {/* Desktop View - unchanged buttons */}
      {!isMobile && (
        <div className="absolute bottom-20 lg:bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3 md:gap-4 z-30 ">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-orange-600 bg-opacity-90 text-[#ffff] text-base sm:text-lg md:text-xl 
              font-semibold px-3 sm:px-4 md:px-6 py-2 sm:py-2 md:py-3 rounded-3xl  shadow-lg hover:bg-opacity-80  transition-all duration-300"
          >
            {t('bookSlot')}
          </button>
          <button
            onClick={() => setIsFrameModalOpen(true)}
            className="bg-white bg-opacity-60 text-black text-base sm:text-lg md:text-xl font-semibold px-3 sm:px-4 md:px-6 py-2 sm:py-2 md:py-3 rounded-3xl shadow-lg hover:bg-opacity-100 transition-all duration-300"
          >
            {t('photoFrames')}
          </button>
        </div>
      )}

      {isMobile && (
        <div className="absolute justify-center bottom-7 sm:bottom-7 md:bottom-7 left-0 right-0 flex z-30">
          <div className="relative">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="bg-orange-700 bg-opacity-80 text-[#ffff] z-40
                 text-base sm:text-lg font-semibold px-4 sm:px-5 py-2 sm:py-2.5 rounded-3xl shadow-lg hover:bg-opacity-50 transition-all"
            >
              Book Now
            </motion.button>

            <AnimatePresence>
              {isMobileMenuOpen && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 sm:mb-3 flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => {
                      setIsModalOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="bg-white text-black flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg shadow-lg hover:bg-gray-100 transition-all whitespace-nowrap text-sm sm:text-base"
                  >
                    <FaCalendarAlt />
                    {t('bookSlot')}
                  </motion.button>

                  <motion.button
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => {
                      setIsFrameModalOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="bg-white text-black flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg shadow-lg hover:bg-gray-100 transition-all whitespace-nowrap text-sm sm:text-base"
                  >
                    <FaImages />
                    {t('photoFrames')}
                  </motion.button>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
      {/* </div> */}

      {/* Book Slot Modal - unchanged from your original */}
      {isModalOpen && (
        <AnimatePresence>
          <div
            className="fixed inset-0 bg-black  bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              key="bookSlotModal"
              initial={{ y: '-60px' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ duration: 0.2, ease: 'linear' }}
              className={`rounded-2xl text-left p-4 sm:p-6 md:p-8 w-[80%] lg:max-w-lg shadow-2xl border border-orange-300 ${uploadedImages.length > 0 ? 'bg-gradient-to-b from-gray-100 to-orange-400 shadow-lg' : 'bg-gradient-to-b from-white to-orange-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-600 text-sm sm:text-sm md:text-base lg:text-lg font-bold float-right sticky hover:bg-gray-200 hover:bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center"
              >
                ✖
              </button>
              <form onSubmit={handleFormSubmit}>
                <h2 className="text-gray-700 text-sm sm:text-sm md:text-base lg:text-lg font-bold text-center mb-4 sm:mb-6">{t('bookSlotFormTitle')}</h2>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.name')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('Enter your name')}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    required
                  />
                </div>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.phoneNumber')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      let input = e.target.value.replace(/\D/g, "");
                      input = input.slice(0, 10);
                      setFormData((prev) => ({ ...prev, phone: input }));
                    }}
                    placeholder={t('Enter your phone number')}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    required
                  />
                </div>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.eventDate')}</label>
                  <input
                    type="date"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                    placeholder={t('Select the event date')}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.eventTime')}</label>
                  <input
                    type="time"
                    name="eventTime"
                    value={formData.eventTime}
                    onChange={handleInputChange}
                    placeholder={t('Select the event time')}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    required
                  />
                </div>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.eventType')}</label>
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    required
                  >
                    <option value="" disabled>{t('Select the type of event')}</option>
                    {categories.map((category, index) => (
                      <option key={index} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
                {error && <p className="text-red-500 mb-2 sm:mb-4 text-sm sm:text-sm md:text-base lg:text-lg">{error}</p>}
                <div className="flex justify-center items-center">
                  <button
                    type="submit"
                    className={`w-28 sm:w-32 md:w-36 py-1 sm:py-2 md:py-3 text-[#ffff] rounded-3xl ${loading ? 'bg-gray-400' : 'bg-orange-600 opacity-70 hover:opacity-100'} shadow-2xl hover:shadow-2xl text-sm sm:text-sm md:text-base lg:text-lg`}
                    disabled={loading}
                  >
                    {loading ? t('submitting') : t('submitBtn')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </AnimatePresence>
      )}

      {isFrameModalOpen && (
        <AnimatePresence>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setIsFrameModalOpen(false)}
          >
            <motion.div
              key="frameModal"
              initial={{ y: '-70px' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ duration: 0.2, ease: 'linear' }}
              className={`rounded-lg p-4 text-left sm:p-6 md:p-8 w-[80%] lg:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl border border-orange-300 ${uploadedImages.length > 0 ? 'bg-gradient-to-b from-gray-100 to-orange-400 shadow-lg' : 'bg-gradient-to-b from-white to-orange-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsFrameModalOpen(false)}
                className="text-gray-600 text-sm sm:text-sm md:text-base lg:text-lg font-bold float-right sticky hover:bg-gray-200 hover:bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center"
              >
                ✖
              </button>
              <form onSubmit={handleFrameFormSubmit}>
                <h2 className="text-gray-700 text-sm sm:text-sm md:text-base lg:text-lg font-bold text-center mb-4 sm:mb-6">{t('photoFramesFormTitle')}</h2>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.name')}</label>
                  <input
                    type="text"
                    name="name"
                    value={frameFormData.name}
                    onChange={handleFrameInputChange}
                    placeholder={t('Enter your name')}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    required
                  />
                </div>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.phoneNumber')}</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={frameFormData.phoneNumber}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (input.length <= 10) {
                        handleFrameInputChange(e);
                      }
                    }}
                    placeholder={t('Enter your phone number')}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    required
                  />
                </div>
                <div className="mb-2 sm:mb-4 flex items-center">
                  <label className="block text-gray-700 w-1/3 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.frameSize')}</label>
                  <select
                    name="frameSize"
                    value={frameFormData.frameSize}
                    onChange={handleFrameInputChange}
                    className="w-2/3 px-2 sm:px-3 md:px-4 py-1 sm:py-2 md:py-3 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs sm:text-xs md:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-orange-300"
                    required
                  >
                    <option value="" disabled>{t('Choose the frame size')}</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div className="mb-2 sm:mb-4">
                  <label className="block text-gray-700 mb-1 sm:mb-2 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.uploadPhotos')}</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="w-full text-sm sm:text-sm md:text-base lg:text-lg"
                  />
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 sm:gap-2 md:gap-3 mt-2 sm:mt-4">
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Uploaded ${index + 1}`}
                          className="w-full h-16 sm:h-20 md:h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-sm sm:text-sm md:text-base lg:text-lg"
                        >
                          ✖
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-2 sm:mb-4">
                  <label className="block text-gray-700 text-sm sm:text-sm md:text-base lg:text-lg">{t('formLabels.selectFrame')}</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1 sm:gap-2 md:gap-3">
                    {frameOptions.map((frame, index) => (
                      <div
                        key={index}
                        className={`cursor-pointer border-2 ${frameFormData.selectedFrame === frame ? 'border-orange-300' : 'border-gray-300'} rounded-lg p-1 sm:p-2 md:p-3 relative overflow-hidden`}
                        onClick={() => handleFrameSelection(frame)}
                      >
                        <img
                          src={frame}
                          alt={`Frame ${index + 1}`}
                          className="w-full h-16 sm:h-20 md:h-24 rounded-lg transition-transform duration-300 ease-in-out transform hover:scale-150"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-center items-center">
                  <button
                    type="submit"
                    className={`w-28 sm:w-32 md:w-36 py-1 sm:py-2 md:py-3 text-[#ffff] rounded-3xl ${loading ? 'bg-gray-400' : 'bg-orange-600 opacity-70 hover:opacity-100'} shadow-2xl hover:shadow-2xl text-sm sm:text-sm md:text-base lg:text-lg`}
                    disabled={loading}
                  >
                    {loading ? t('submitting') : t('submitBtn')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default Home;